import React, { useState, useEffect, useRef } from 'react';
import { MoreVertical } from 'lucide-react';

type ItemProps = {
  itemId: string;
  currentSelectId: string;
  displayText: string;
  textColor?: string;
  hoverTextColor?: string;
  hoverBackGround?: string;
  isOpen: boolean;
  setOpenItemId: (id: string | null) => void;
  setItemId: (id: string) => void;
  renderButtons: (itemId: string) => React.ReactNode;
  disable: boolean;
};

const truncateText = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
};

const Item: React.FC<ItemProps> = ({
  itemId,
  currentSelectId,
  displayText,
  textColor = 'text-BADBlue',
  hoverTextColor = 'hover:text-BAWhite',
  hoverBackGround = 'hover:bg-BAMPink',
  isOpen,
  setOpenItemId,
  setItemId,
  renderButtons,
  disable,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ x: 0, y: 0 });

  const renderBGActiveColor = itemId === currentSelectId ? 'bg-BAMPink': ''
  const renderTextActiveColor = itemId === currentSelectId ? 'text-BAWhite': textColor
  const disableCursor = disable ? 'opacity-50 cursor-not-allowed' : '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (itemRef.current && !itemRef.current.contains(event.target as Node)) {
        setOpenItemId(null);
      }
    };

    const handleEscapePress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenItemId(null);
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleEscapePress);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscapePress);
    };
  }, [isOpen, setOpenItemId]);

  const handleButtonClick = (event: React.MouseEvent) => {
    if (!disable) {
      setOpenItemId(isOpen ? null : itemId);
      setDropdownPosition({ x: event.clientX + 15, y: event.clientY - 10 });
    }
  };

  const handleItemClick = () => {
    if (!disable) {
      setItemId(itemId);
    }
  };

  return (
    <div
      key={itemId}
      ref={itemRef}
      className={`px-4 py-2 rounded-3xl text-xs sm:text-sm md:text-base ${textColor} ${hoverTextColor} ${hoverBackGround} ${renderBGActiveColor} ${renderTextActiveColor} ${disableCursor}`}
    >
      <div className="flex justify-between items-center">
        <button
          className={`w-[80%] text-left ${disableCursor}`}
          onClick={handleItemClick}
          disabled={disable}
        >
          {truncateText(displayText, 30)}
        </button>
        <button
          onClick={handleButtonClick}
          className={`p-1 rounded-full hover:bg-gray-300 ${disableCursor}`}
          disabled={disable}
        >
          <MoreVertical />
        </button>
        {isOpen && !disable && (
          <>
            <div
              className="fixed inset-0 bg-black opacity-50 z-10"
              onClick={() => setOpenItemId(null)}
            />
            <div
              className="fixed bg-BAWhite rounded shadow-lg py-1 z-20 w-[80vw] md:w-36 left-1/2 transform -translate-x-1/2 md:left-auto md:transform-none"
              style={{
                top: `${Math.min(dropdownPosition.y, window.innerHeight - 200)}px`,
                left: window.innerWidth < 800 ? '50%' : `${dropdownPosition.x}px`,
                transform: window.innerWidth < 800 ? 'translateX(-50%)' : 'none'
              }}
            >
              {renderButtons(itemId)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Item;